import PrivacyPolicyHtml from '../../html/privacyPolicyPopup.html';
import TermsOfServiceHtml from '../../html/termsOfServicePopup.html';
import BaseView from '../baseView';
import HwpCollection from '../data/hwpCollection';
import Dialog, { EVENTS as DIALOG_EVENTS } from '../dialog';
import BaseGameView from '../engine/baseGameView';
import GameFactory from '../gameFactory';
import LinkHandler from '../linkHandler';
import { getLogger } from '../logging/logger';
import InputManager from './inputManager';

const logger = getLogger('PopUpManager');

/**
 * @typedef {import('../engine/baseGame.types').BaseGameViewOptions} BaseGameViewOptions
 * @typedef {import('../baseView').BaseViewOptions} BaseViewOptions
 * @typedef {import('../dialog').DialogOptions} DialogOptions
 * @typedef {Backbone.Model<DialogOptions>} DialogModel
 * @typedef {HwpCollection<DialogModel>} DialogCollection
 *
 * @typedef {object} PopUpManagerOptions
 * @property {string} [contentUrl]
 * @property {(DialogOptions | BaseViewOptions)[]} [popUps]
 */

/**
 * @type {readonly DialogOptions[]}
 */
export const PredefinedDialogs = Object.freeze([
	{
		id: 'privacyPolicy',
		template: PrivacyPolicyHtml,
		className: 'full-width',
		btns: [{ id: 'close', text: ['Close'], className: 'btn btn-close' }],
	},
	{
		id: 'termsOfService',
		template: TermsOfServiceHtml,
		className: 'full-width',
		btns: [{ id: 'close', text: ['Close'], className: 'btn btn-close' }],
	},
]);

/** @enum {typeof EVENTS[keyof typeof EVENTS]} */
export const EVENTS = Object.freeze({
	dialogOpened: 'dialogOpened',
	dialogClosed: 'dialogClosed',
});

class PopUpManager extends LinkHandler {
	/**
	 * @type {DialogCollection}
	 * @description Collection of known dialogs that can be displayed
	 */
	popUps = new HwpCollection();

	/**
	 * @type {Dialog | undefined}
	 * @description The currently displayed dialog
	 */
	activeDialog = undefined;

	/**
	 * @type {DialogOptions[]}
	 * @description The queue of dialogs to be displayed
	 */
	dialogQueue = [];

	/** @type {BaseGameView<any> | undefined} */
	activeGame = undefined;

	/** @type {BaseGameViewOptions[]} */
	gameQueue = [];

	get linkPattern() {
		return /popup:\/\/(\w+)/;
	}

	/**
	 * @param {PopUpManagerOptions} [options]
	 */
	constructor(options) {
		super();
		logger.log(`[constructor]`, { options });

		this.dialogOverlay = new BaseView({
			id: 'popup-overlay',
			events: {
				// Is this wrong? Should it be dismissed somewhere else?
				// 'click  button': this.dismissDialog.bind(this),
			},
		});

		this.gameOverlay = new BaseView({
			id: 'game-overlay',
			events: {
				'click .close-game button': this.dismissGame.bind(this),
				'click .btn.close-game': this.dismissGame.bind(this),
			},
		});

		this.initialize(options);
	}

	/**
	 * @param {PopUpManagerOptions} [options]
	 */
	initialize(options = {}) {
		logger.log(`[${this.initialize.name}]`, { ...options });

		const { contentUrl, popUps = [] } = options;

		// Reset and prepopulate with known dialogs
		this.popUps.reset([...PredefinedDialogs, ...popUps]);

		if (contentUrl) {
			this.popUps.fetch({
				url: contentUrl,
				success: () => this.trigger('initialized'),
				error: (error) => logger.error(`[Error loading data]`, { error }),
			});
		}
	}

	get isDisplayed() {
		logger.log(`[isDisplayed]`);
		return !!this.activeDialog && !this.activeGame;
	}

	/**
	 * @param {string} uri
	 */
	handleLink(uri) {
		logger.log(`[${this.handleLink.name}]`, { uri });

		const [, popUpId] = this.linkPattern.exec(uri) ?? [];
		const { id, ...popUpData } = this.popUps.get(popUpId)?.toJSON();
		if (!popUpData) {
			logger.warn(`[Popup "${popUpId}" not found]`);
			return;
		}

		if (popUpData.type) {
			this.launchGame(popUpData);
		} else {
			this.displayDialog(popUpData);
		}
	}

	/**
	 * Displays a dialog to the user. If a dialog is already active, the supplied dialog will be queued.
	 * When a Dialog is dismissed and another dialog is queued, the overlay will stay displayed and swap
	 * out only the Dialog to create a better user experience.
	 *
	 * @param {DialogOptions} dialogData An object that must contain 'message' or
	 * 'contentHtml' properties and optionally can provided Backbone View Events object
	 * for handling mutliple buttons.
	 * @returns Returns the new dialog created for reference
	 */
	async displayDialog(dialogData) {
		logger.log(`[${this.displayDialog.name}]`, { dialogData });

		if (this.activeDialog) {
			this.dialogQueue.push(dialogData);
			return;
		}

		// Make sure our overlay has been added to the DOM
		if (!this.dialogOverlay.el.parentNode) {
			await this.dialogOverlay.dismissView({ duration: 0 });
			$('body').prepend(this.dialogOverlay.$el);
		}
		this.activeDialog = new Dialog(dialogData);
		this.activeDialog.once(DIALOG_EVENTS.closeClicked, () => this.dismissDialog());

		// If the overlay is displayed, we will need to fade out the Dialog, add it to the DOM, then fade it
		const overlayVisibility = this.dialogOverlay.$el.css('visibility');
		if (overlayVisibility !== 'hidden') {
			await this.activeDialog.dismissView({ duration: 0 });
			this.dialogOverlay.$el.append(this.activeDialog.$el);
			await this.activeDialog.displayView();
		} else {
			this.dialogOverlay.$el.append(this.activeDialog.$el);
			await this.dialogOverlay.displayView();
		}

		this.activeDialog?.$('button').trigger('focus');

		this.trigger(EVENTS.dialogOpened, this.activeDialog);
	}

	async dismissDialog() {
		logger.log(`[${this.dismissDialog.name}]`, { activeDialog: this.activeDialog });

		if (this.activeDialog) {
			if (this.dialogQueue.length && this.activeDialog) {
				await this.activeDialog.dismissView();
			} else {
				await this.dialogOverlay.dismissView();
			}

			this.trigger(EVENTS.dialogClosed, this.activeDialog);

			this.dialogOverlay.$el.empty();
			this.activeDialog?.remove();
			this.activeDialog?.destroy();
			this.activeDialog = undefined;

			const queuedDialog = this.dialogQueue.shift();
			if (queuedDialog) {
				this.displayDialog(queuedDialog);
			} else {
				// Refocus whatever the default section is
				InputManager.focus();
			}
		}
	}

	/**
	 * Launches a game from the given data, or queues one to be played if another is already active
	 * @param {BaseGameViewOptions} gameData
	 */
	async launchGame(gameData) {
		logger.log(`[${this.launchGame.name}]`, { gameData });

		if (this.activeGame) {
			logger.warn('Another activity is already in progress');
			this.gameQueue.push(gameData);
			return;
		}

		// Make sure our overlay has been added to the DOM
		if (!this.gameOverlay.el.parentNode) {
			await this.gameOverlay.dismissView({ duration: 0 });
			$('body').prepend(this.gameOverlay.$el);
		}

		this.activeGame = await GameFactory.createGame(gameData);

		// If the overlay is displayed, we will need to fade out the Dialog, add it to the DOM, then fade it
		const overlayVisibility = this.gameOverlay.$el.css('visibility');
		if (overlayVisibility !== 'hidden') {
			await this.activeGame.dismissView({ duration: 0 });
			this.gameOverlay.$el.append(this.activeGame.$el);
			await this.activeGame.displayView();
		} else {
			this.gameOverlay.$el.append(this.activeGame.$el);
			await this.gameOverlay.displayView();
		}
	}

	async dismissGame() {
		logger.log(`[${this.dismissGame.name}]`);

		if (this.gameOverlay.el.parentNode) {
			await this.gameOverlay.dismissView();
		}

		this.gameOverlay.$el.remove();
		this.gameOverlay.$el.empty();
		this.activeGame?.destroy();
		this.activeGame = undefined;

		const queuedGame = this.gameQueue.shift();
		if (queuedGame) {
			logger.info('[activating queued game]', { queuedGame });
			this.launchGame(queuedGame);
		}
	}
}

export default new PopUpManager();
